import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router";
import { Activity, Cpu, FolderGit2, Globe, HeartPulse, LayoutGrid, LogOut, Menu, Network, Package, RefreshCw, ScrollText, Search, Settings as SettingsIcon, WifiOff, X } from "lucide-react";

import { api } from "../lib/api.js";
import { useLive, useLiveState } from "../lib/events.js";
import { CommandPalette } from "./CommandPalette.jsx";
import { NotificationBell, NotificationPanel, useInbox } from "./NotificationCenter.jsx";

/**
 * The application shell.
 *
 * The navigation is grouped, and the grouping is the information architecture:
 * what ships code, what the box is made of, what governs access. Eight
 * destinations do not fit a top bar, and the previous one broke outright the
 * moment the "database unavailable" badge appeared — it had no `flex-wrap` and was
 * already at the width of a phone.
 *
 * The footer is the reason this is a sidebar rather than a wider top bar: box
 * health belongs on every page, not only on the one page that happens to show it.
 */

const NAV = [
    {
        group: "Deploy",
        items: [
            { to: "/", label: "Overview", Icon: LayoutGrid, end: true },
            { to: "/projects", label: "Projects", Icon: Package },
            { to: "/deployments", label: "Deployments", Icon: Activity },
            { to: "/workspaces", label: "Workspaces", Icon: FolderGit2 }
        ]
    },
    {
        group: "Machine",
        items: [
            // Carries the monitor's dot: green when everything answers.
            { to: "/status", label: "Status", Icon: HeartPulse },
            { to: "/processes", label: "Processes", Icon: Cpu },
            { to: "/ports", label: "Ports", Icon: Network },
            { to: "/domains", label: "Domains", Icon: Globe }
        ]
    },
    {
        group: "Admin",
        items: [
            { to: "/audit", label: "Audit", Icon: ScrollText },
            { to: "/settings", label: "Settings", Icon: SettingsIcon }
        ]
    }
];

export function AppShell({ actor, onSignedOut, children }) {
    const [health, setHealth] = useState(null);
    const [system, setSystem] = useState(null);
    const [status, setStatus] = useState(null);
    const [activity, setActivity] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [inboxOpen, setInboxOpen] = useState(false);
    const inbox = useInbox();
    const location = useLocation();
    const live = useLiveState();

    // Each of the footer's and the nav's numbers follows the event that moves
    // it: the box on every sample, the dot on every monitor pass, the counts
    // when a deployment or a session changes. None of them is asked on a timer.
    useLive(["system"], () => {
        api.system.health().then(setHealth).catch(() => setHealth({ ok: false, database: "unreachable" }));
        api.system.info().then(setSystem).catch(() => setSystem(null));
    });
    // Answered from the monitor's memory, so it works while Mongo does not.
    useLive(["status"], () => api.status.summary().then(setStatus).catch(() => setStatus(null)));
    useLive(["deployments", "sessions"], () => api.activity.get().then(setActivity).catch(() => setActivity(null)));

    // A drawer that survives navigation is a drawer in the way.
    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!drawerOpen) return undefined;
        const onKey = (event) => event.key === "Escape" && setDrawerOpen(false);
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [drawerOpen]);

    /**
     * The two global keys.
     *
     * Both are ignored while you are typing, or the moment you tried to put a
     * slash in a search box it would open something instead.
     */
    useEffect(() => {
        const onKey = (event) => {
            const typing = /^(input|textarea|select)$/i.test(event.target?.tagName ?? "") || event.target?.isContentEditable;

            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
                event.preventDefault();
                setPaletteOpen((value) => !value);
                return;
            }

            if (event.key === "/" && !typing && !paletteOpen) {
                const search = document.querySelector("[data-page-search]");
                if (search) {
                    event.preventDefault();
                    search.focus();
                }
            }
        };

        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [paletteOpen]);

    return (
        <div className="min-h-screen md:flex">
            {/* Mobile: a slim bar, because the sidebar is off-canvas here. */}
            {/*
             * Opaque, and no backdrop-filter. iOS 27 paints its own translucent
             * band across the top of a standalone web app, and anything
             * translucent underneath composites *through* it — so a bar at
             * `bg-white/90` washed out, wordmark, icons and all, while the page
             * below it stayed sharp. Frosted glass is not available here: the
             * system is already using it, above us. Do not reintroduce a slash
             * opacity or a blur on this bar.
             */}
            <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-2.5 pt-app-bar md:hidden dark:border-gray-800 dark:bg-gray-950">
                <button onClick={() => setDrawerOpen(true)} aria-label="Open navigation" className="muted hover:text-gray-900 dark:hover:text-gray-100">
                    <Menu size={18} />
                </button>
                <Wordmark />
                <button onClick={() => setPaletteOpen(true)} aria-label="Open the command palette" className="muted ml-auto">
                    <Search size={17} />
                </button>
                <NotificationBell unread={inbox.unread} onClick={() => setInboxOpen(true)} className="mr-1" />
                <Link to="/status" aria-label={statusLabel(status, health)} className="flex items-center p-1">
                    <StatusDot status={status} health={health} />
                </Link>
            </header>

            {drawerOpen ? (
                <div className="fixed inset-0 z-40 md:hidden">
                    <button className="absolute inset-0 bg-black/50" onClick={() => setDrawerOpen(false)} aria-label="Close navigation" tabIndex={-1} />
                    <div className="absolute inset-y-0 left-0 w-64 overflow-y-auto border-r border-gray-200 bg-white pt-app-bar dark:border-gray-800 dark:bg-gray-950">
                        <Sidebar
                            actor={actor}
                            health={health}
                            status={status}
                            activity={activity}
                            system={system}
                            unread={inbox.unread}
                            onSignedOut={onSignedOut}
                            onOpenPalette={() => { setDrawerOpen(false); setPaletteOpen(true); }}
                            onOpenInbox={() => { setDrawerOpen(false); setInboxOpen(true); }}
                            showLabels
                            onClose={() => setDrawerOpen(false)}
                        />
                    </div>
                </div>
            ) : null}

            {/* Tablet collapses to an icon rail; desktop gets the full sidebar. */}
            <aside className="sticky top-0 hidden h-screen shrink-0 border-r border-gray-200 pt-[env(safe-area-inset-top)] md:block md:w-14 lg:w-60 dark:border-gray-800">
                <Sidebar
                    actor={actor}
                    health={health}
                    status={status}
                    activity={activity}
                    system={system}
                    unread={inbox.unread}
                    onSignedOut={onSignedOut}
                    onOpenPalette={() => setPaletteOpen(true)}
                    onOpenInbox={() => setInboxOpen(true)}
                />
            </aside>

            <main className="min-w-0 flex-1 px-4 py-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:px-6 lg:px-8">
                <div className="mx-auto max-w-7xl">{children}</div>
            </main>

            <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
            <NotificationPanel open={inboxOpen} onClose={() => setInboxOpen(false)} inbox={inbox} />
            <StreamNotice live={live} />
        </div>
    );
}

/**
 * The two things the stream can say about itself.
 *
 * Down for more than a moment: the page is stale and says so, on the same
 * corner the toasts use. A newer build waiting: the tab would have reloaded
 * itself already if nobody were typing, so what is left is to offer it.
 */
function StreamNotice({ live }) {
    const [stale, setStale] = useState(false);
    useEffect(() => {
        if (live.connected) {
            setStale(false);
            return undefined;
        }
        const timer = setTimeout(() => setStale(true), 4_000);
        return () => clearTimeout(timer);
    }, [live.connected]);

    if (!stale && !live.update) return null;
    return (
        <div className="pointer-events-none fixed inset-x-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-40 flex flex-col items-end gap-2 sm:inset-x-auto sm:right-4">
            {live.update ? (
                <div className="card pointer-events-auto flex w-full max-w-sm items-center gap-3 px-3 py-2 text-sm shadow-lg" data-update={live.update}>
                    <RefreshCw size={15} className="shrink-0 text-blue-500" aria-hidden />
                    <span className="min-w-0 flex-1 text-gray-700 dark:text-gray-200">ops was updated. Reload when you are done here.</span>
                    <button className="btn-primary px-2.5 py-1" onClick={live.reload}>Reload</button>
                </div>
            ) : null}
            {stale ? (
                <div className="card pointer-events-auto flex w-full max-w-sm items-center gap-2 px-3 py-2 text-sm shadow-lg" data-stale>
                    <WifiOff size={15} className="shrink-0 text-amber-500" aria-hidden />
                    <span className="text-gray-700 dark:text-gray-200">Reconnecting — what you see may be stale.</span>
                </div>
            ) : null}
        </div>
    );
}

function Sidebar({ actor, health, status, activity, system, unread, onSignedOut, onOpenPalette, onOpenInbox, showLabels = false, onClose }) {
    // In the rail the label is visually hidden, so each link carries an aria-label.
    // The old top bar hid the text with `hidden sm:inline` and marked the icons
    // aria-hidden, which left five unnamed glyphs for a screen reader.
    const labelClass = showLabels ? "" : "hidden lg:inline";

    return (
        <div className="flex h-full flex-col">
            {/*
             * The rail is 56px wide, so the mark and the bell stack there and
             * sit in a row from `lg`, where the wordmark takes the mark's place.
             */}
            <div className={`flex items-center px-3 py-5 ${showLabels ? "gap-2" : "flex-col gap-3 lg:flex-row lg:gap-2"}`}>
                <Wordmark className={showLabels ? "" : "hidden lg:block"} onClick={onClose} />
                {/* In the rail there is no room for a wordmark, so the mark stands in. */}
                <img src="/favicon.svg" alt="" className={`size-6 ${showLabels ? "hidden" : "lg:hidden"}`} />
                {/*
                 * The bell is not a destination, so it does not belong in the
                 * menu; it lives at the end of the header row, as it does on
                 * the phone bar — which is why the drawer does not repeat it.
                 */}
                <NotificationBell unread={unread} onClick={onOpenInbox} className={showLabels ? "hidden" : "lg:ml-auto"} />
                {onClose ? (
                    <button onClick={onClose} className="muted ml-auto hover:text-gray-900 dark:hover:text-gray-100" aria-label="Close navigation">
                        <X size={16} />
                    </button>
                ) : null}
            </div>

            {/*
             * Only outside the drawer.
             *
             * `showLabels` is the drawer, and on a phone the top bar already
             * carries search a thumb's width above this — the same control
             * twice on one screen, one of them below the fold.
             */}
            <div className={`px-3 pb-3 ${showLabels ? "hidden" : ""}`}>
                {/*
                  * Shaped like the input it stands in for. As a nav row it read as
                  * a destination; what it actually does is put a cursor somewhere.
                  */}
                <button
                    onClick={onOpenPalette}
                    className={`flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 text-sm text-gray-500
                                transition-colors hover:border-gray-300 hover:bg-white
                                dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400 dark:hover:border-gray-700 dark:hover:bg-gray-800
                                ${showLabels ? "" : "justify-center lg:justify-start"}`}
                    aria-label="Open the command palette"
                >
                    <Search size={15} className="shrink-0" aria-hidden />
                    <span className={labelClass}>Search</span>
                    <kbd className={`ml-auto rounded border border-gray-300 px-1 font-sans text-[10px] text-gray-400 dark:border-gray-700 dark:text-gray-500 ${showLabels ? "" : "hidden lg:inline"}`}>
                        ⌘K
                    </kbd>
                </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-3">
                {NAV.map(({ group, items }) => (
                    <div key={group}>
                        <p className={`nav-group ${showLabels ? "" : "hidden lg:block"}`}>{group}</p>
                        {items.map(({ to, label: text, Icon, end }) => {
                            const badge = badgeFor(to, text, { status, health, activity });
                            const count = badge?.count ?? 0;
                            const shown = count > 9 ? "9+" : count;
                            return (
                                <NavLink
                                    key={to}
                                    to={to}
                                    end={end}
                                    title={badge?.label ?? text}
                                    aria-label={badge?.label ?? text}
                                    className={({ isActive }) => (isActive ? "nav-item-active" : "nav-item")}
                                >
                                    <span className="relative shrink-0">
                                        <Icon size={16} aria-hidden />
                                        {/* Status is a verdict even at zero, so its dot is always there; a count only exists when it is not zero. */}
                                        {badge?.dot ? <StatusDot status={status} health={health} className="absolute -right-1 -top-0.5 ring-2 ring-white dark:ring-gray-950" /> : null}
                                        {count > 0 && !badge.dot ? (
                                            <span className={`absolute -right-2 -top-1.5 min-w-3.5 rounded-full px-1 text-center text-[9px] font-medium leading-3.5 text-white ring-2 ring-white dark:ring-gray-950 ${badge.tone} ${showLabels ? "hidden" : "lg:hidden"}`} aria-hidden>
                                                {shown}
                                            </span>
                                        ) : null}
                                    </span>
                                    <span className={labelClass}>{text}</span>
                                    {count > 0 ? (
                                        <span className={`ml-auto rounded-full px-1.5 text-[10px] font-medium leading-4 text-white ${badge.tone} ${showLabels ? "" : "hidden lg:inline"}`} data-nav-count={count}>
                                            {shown}
                                        </span>
                                    ) : null}
                                </NavLink>
                            );
                        })}
                    </div>
                ))}
            </nav>

            <div className="border-t border-gray-200 px-3 py-3.5 dark:border-gray-800">
                <BoxHealth health={health} system={system} compact={!showLabels} />
                <div className={`mt-3 flex items-center gap-2 ${showLabels ? "" : "hidden lg:flex"}`}>
                    <span className="muted min-w-0 flex-1 truncate text-xs">{actor}</span>
                    <button
                        onClick={async () => {
                            await api.auth.logout();
                            onSignedOut();
                        }}
                        className="muted hover:text-gray-900 dark:hover:text-gray-100"
                        title="Sign out"
                        aria-label="Sign out"
                    >
                        <LogOut size={15} />
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * What an item carries besides its name.
 *
 * Status carries the monitor's verdict the way the bell carries the unread
 * count; Deployments the deploys in flight; Workspaces the sessions alive. In
 * the rail that is a dot or a small number on the icon, and beside the label a
 * count, where there is room for one. The label is what a screen reader and a
 * hover get: the count as a sentence.
 */
function badgeFor(to, text, { status, health, activity }) {
    if (to === "/status") {
        return { dot: true, count: status?.down ?? 0, tone: "bg-red-600", label: statusLabel(status, health) };
    }
    if (to === "/deployments") {
        const count = activity?.deployments ?? 0;
        return { count, tone: "bg-blue-600", label: count > 0 ? `${text}: ${count} in progress` : text };
    }
    if (to === "/workspaces") {
        const count = activity?.sessions ?? 0;
        return { count, tone: "bg-green-600", label: count > 0 ? `${text}: ${count} session${count === 1 ? "" : "s"} running` : text };
    }
    return null;
}

/**
 * The Scaling wordmark. Two files rather than one: the colour version carries a
 * gradient tuned for a light ground and the white version is for a dark one, so
 * each theme gets the one that was drawn for it.
 *
 * The caller's visibility class goes on the wrapper, not on the images. Put on
 * both images, a caller's `lg:block` beat the white one's `hidden dark:block` and
 * painted it over the colour one — a ghost wordmark in the sidebar on any screen
 * wide enough, in the light theme only.
 */
function Wordmark({ className = "", onClick }) {
    return (
        /*
         * The visibility class stays on this wrapper and never reaches the
         * images. When `hidden lg:block` landed on both of them it beat the white
         * one's `hidden dark:block` and painted it over the colour one.
         */
        <span className={className}>
            <Link
                to="/"
                onClick={onClick}
                aria-label="scaling-ops — go to the overview"
                className="inline-flex items-center gap-1.5 rounded-sm"
            >
                <span className="inline-block h-5">
                    <img src="/logo.svg" alt="" className="block h-full dark:hidden" />
                    <img src="/logo-white.svg" alt="" className="hidden h-full dark:block" />
                </span>
                {/* The mark says Scaling; the product is scaling-ops. */}
                <span className="text-base font-semibold tracking-tight text-gray-500 dark:text-gray-400">ops</span>
            </Link>
        </span>
    );
}

function HealthDot({ health, className = "" }) {
    const ok = health?.database === "connected";
    return (
        <span
            className={`inline-block size-2 shrink-0 rounded-full ${ok ? "bg-green-500" : health ? "bg-red-500" : "bg-gray-300 dark:bg-gray-600"} ${className}`}
            title={ok ? "Database connected" : "Database unavailable"}
        />
    );
}

/**
 * The box in one dot: green when everything the monitor asks is answering,
 * red when something is not, gray before the first pass. The database being
 * unreachable is red too, whatever the monitor's memory says — the page you are
 * looking at is stale.
 */
function statusLabel(status, health) {
    if (health && health.database !== "connected") return "Status: database unavailable";
    if (!status || status.state === "unknown") return "Status: not checked yet";
    return status.down > 0 ? `Status: ${status.down} not answering` : "Status: all systems operational";
}

function StatusDot({ status, health, className = "" }) {
    const dbDown = health && health.database !== "connected";
    const tone = dbDown || status?.state === "down"
        ? "bg-red-500"
        : status?.state === "operational"
            ? "bg-green-500"
            : "bg-gray-300 dark:bg-gray-600";
    return <span className={`inline-block size-2 shrink-0 rounded-full ${tone} ${className}`} data-status-dot={dbDown ? "down" : status?.state ?? "unknown"} />;
}

/**
 * Is the box all right — answerable from every page.
 *
 * The database line is not cosmetic: when Mongo is down the webhook still deploys
 * from the on-disk routing snapshot, but nothing is being recorded, so everything
 * else the interface says is stale.
 */
function BoxHealth({ health, system, compact }) {
    const dbOk = health?.database === "connected";
    const processes = system?.processes;
    const memory = system?.memory;

    /*
     * The rail has room for a dot and the sidebar has room for the detail, and the
     * same component serves both — so it renders both and lets the breakpoint
     * choose. It used to return early on `compact`, which is true for the desktop
     * sidebar too, so the health footer never appeared on a desktop at all: the one
     * thing the redesign added a footer for.
     */
    if (compact) {
        return (
            <>
                <div className="flex justify-center lg:hidden">
                    <HealthDot health={health} />
                </div>
                <div className="hidden lg:block">
                    <BoxHealth health={health} system={system} compact={false} />
                </div>
            </>
        );
    }

    return (
        <div className="space-y-1">
            <div className="flex items-center gap-2 text-xs">
                <HealthDot health={health} />
                <span className={dbOk ? "muted" : "font-medium text-red-600 dark:text-red-400"}>
                    {dbOk ? "Database connected" : "Database unavailable"}
                </span>
            </div>
            {processes ? (
                <p className="muted text-xs tabular-nums">
                    {processes.online}/{processes.total} processes
                    {processes.stopped > 0 ? <span className="ml-1 text-amber-600 dark:text-amber-400">· {processes.stopped} down</span> : null}
                </p>
            ) : null}
            {memory ? (
                <p className="muted text-xs tabular-nums">{(memory.freeBytes / 1e9).toFixed(1)} GB free</p>
            ) : null}
        </div>
    );
}
